import { buildConversationPath } from '../../app/Routes/orgRouteUtils';
import { Filter } from './Filter';
import { countries } from './countries';
import { GoToDetails, Copy, Button } from '../../components';
import { useConversations } from './useConversations';
import { Table, SearchPanel, Link } from '../../components';
import { formatDate, getDurationFromSeconds } from '../../utilities';
import { DownloadOutlined } from '@mui/icons-material';

const ConversationsPage = () => {
  const {
    agentsOptions,
    filters,
    setFilters,
    loading,
    conversations,
    pagination,
    setPage,
    formId,
    sort,
    setSort,
    exportConversations,
    exporting,
    organizationId,
  } = useConversations();

  const getCountryLabel = (code: string) =>
    countries.find(item => item.value === code)?.label || code;

  return (
    <>
      <Filter
        values={filters ?? {}}
        onChange={setFilters}
        agentsOptions={agentsOptions}
        formId={formId}
      />
      <Table
        sort={sort}
        onSort={setSort}
        loading={loading}
        verticalAlign="top"
        data={conversations ?? []}
        emptyMessage="No Conversations"
        headersClassName="text-small"
        cellsContentClassName="text-small"
        config={{
          columns: [
            {
              sortName: 'title',
              header: 'Conversation Title',
              cellClassName: 'min-w-[12rem] max-w-[16rem]',
              getValue: ({ title, id }) => (
                <>
                  <Link withIcon withDecoration url={buildConversationPath(organizationId!, id!)}>
                    <div className="flex flex-col gap-2">
                      <span className="line-clamp-3 break-words text-ellipsis overflow-hidden">
                        {title}
                      </span>
                    </div>
                  </Link>
                  <Copy
                    text={id ?? ''}
                    label="Copy ID"
                    className="mr-xs block"
                  />
                </>
              ),
            },
            {
              header: 'Agent Title',
              cellClassName: 'min-w-[12rem] max-w-[16rem]',
              getValue: ({ agent_id }) => (
                <div className="flex flex-col gap-2">
                  <span className="line-clamp-3 break-words text-ellipsis overflow-hidden">
                    {agentsOptions?.find(o => o.value === agent_id)?.label ??
                      '-'}
                  </span>
                  <Copy text={agent_id ?? ''} label="Copy ID" />
                </div>
              ),
            },
            {
              header: 'Country',
              sortName: 'country',
              cellClassName: 'min-w-[4rem] max-w-[12rem]',
              getValue: ({ country }) => (
                <span>{getCountryLabel(country as string) ?? '-'}</span>
              ),
            },
            {
              header: 'Started',
              sortName: 'startedAt',
              cellClassName: 'min-w-[8rem] max-w-[12rem]',
              getValue: ({ started_at }) => formatDate(started_at),
            },
            {
              header: 'Finished',
              sortName: 'endedAt',
              cellClassName: 'min-w-[8rem] max-w-[12rem]',
              getValue: ({ ended_at }) =>
                ended_at ? formatDate(ended_at) : ' - ',
            },
            {
              header: 'Duration',
              sortName: 'duration',
              cellClassName: 'min-w-[10rem] max-w-[14rem]',
              getValue: ({ duration }) =>
                getDurationFromSeconds(duration, true),
            },
            {
              header: 'Messages count',
              sortName: 'messagesCount',
              cellClassName: 'min-w-[8rem] max-w-[12rem]',
              getValue: ({ messages_count }) => messages_count || 0,
            },
            {
              header: 'User phone',
              cellClassName: 'min-w-[8rem] max-w-[16rem]',
              getValue: ({ user_phone }) => user_phone || '-',
            },
            {
              header: 'In/Out trunk phone',
              cellClassName: 'min-w-[8rem] max-w-[16rem]',
              getValue: ({ inbound_phone, outbound_phone }) => {
                let phoneLabel = '-';
                if (inbound_phone || outbound_phone) {
                  phoneLabel = `${inbound_phone ?? '-'}/${outbound_phone ?? '-'}`;
                }
                return <p>{phoneLabel}</p>;
              },
            },
            {
              header: (
                <div className="flex justify-center">
                  <Button
                    onClick={() => exportConversations()}
                    loading={exporting}
                    disabled={exporting}
                    size="empty"
                    type="empty"
                    icon={<DownloadOutlined fontSize="medium" />}
                    tooltip="Export Conversations"
                    className="p-2 hover:bg-select border border-border"
                  />
                </div>
              ),
              cellContentClassName: 'text-right',
              cellClassName: 'max-w-[8rem] min-w-[8rem] align-middle',
              getValue: ({ id }) => (
                <GoToDetails url={buildConversationPath(organizationId!, id!)} />
              ),
              align: 'center',
            },
          ],
        }}
      />
      <div className="mt-sm flex justify-between items-center">
        <SearchPanel {...pagination} withPaginate setPage={setPage} />
      </div>
    </>
  );
};

export default ConversationsPage;
